'use client'

import FooterSection from '@/components/footer-section'
import { useLanguage } from '@/contexts/language-context'
import { translations } from '@/lib/translations'

interface TermsSection {
  number: number
  title: string
  content: string | string[]
}

function renderLine(line: string) {
  if (!line.includes('contact@getyesman.com')) return <>{line}</>
  return (
    <>
      {line.split('contact@getyesman.com').map((part, idx, arr) => (
        <span key={idx}>
          {part}
          {idx < arr.length - 1 && (
            <a href="mailto:contact@getyesman.com" title="Envoyer un email à YesMan" className="text-white underline hover:text-white/80 transition-colors">
              contact@getyesman.com
            </a>
          )}
        </span>
      ))}
    </>
  )
}

export default function TermsPage() {
  const { language } = useLanguage()
  const t = translations[language]
  const sections: TermsSection[] = [
    {
      number: 1,
      title: 'Objet',
      content: "Les présentes Conditions Générales d'Utilisation (CGU) régissent l'accès et l'utilisation de la plateforme YesMan, éditée par ABIZZUP (RCCM Togo : TG-LOM-2020-B-1047, Lomé, Togo). En accédant au service, l'utilisateur accepte sans réserve les présentes conditions.",
    },
    {
      number: 2,
      title: 'Description du service',
      content: "YesMan est une plateforme de management opérationnel assistée par IA, destinée aux PME et organisations africaines. Elle couvre les verticales BTP, Immobilier et Agriculture, et opère principalement via WhatsApp Business API, SMS et email.",
    },
    {
      number: 3,
      title: 'Accès au service',
      content: [
        "3.1 L'accès au service est conditionné à la création d'un compte et à la souscription d'un abonnement.",
        "3.2 L'utilisateur doit fournir des informations exactes et maintenir la confidentialité de ses identifiants.",
        "3.3 YesMan se réserve le droit de suspendre tout compte en cas d'activité suspecte ou de violation des présentes CGU.",
        "3.4 L'accès est réservé aux personnes morales ou physiques agissant dans un cadre professionnel.",
      ],
    },
    {
      number: 4,
      title: 'Utilisation acceptable',
      content: [
        "Il est interdit d'utiliser YesMan pour :",
        "— Envoyer des communications non sollicitées (spam) via WhatsApp ou tout autre canal.",
        "— Collecter des données sans le consentement préalable des destinataires.",
        "— Contourner les limites techniques ou de sécurité de la plateforme.",
        "— Utiliser le service à des fins illégales ou contraires à l'ordre public.",
        "— Revendre, sous-licencier ou exploiter commercialement le service sans autorisation écrite.",
      ],
    },
    {
      number: 5,
      title: 'Propriété intellectuelle',
      content: "YesMan et l'ensemble de ses composants (marque, logiciels, interfaces, algorithmes, contenus) sont la propriété exclusive d'ABIZZUP. Toute reproduction, modification ou exploitation sans autorisation préalable est interdite. Les données opérationnelles saisies par l'utilisateur restent sa propriété.",
    },
    {
      number: 6,
      title: 'Données personnelles',
      content: "Le traitement des données personnelles est régi par la Politique de Confidentialité de YesMan, disponible à l'adresse getyesman.com/fr/privacy. En utilisant le service, l'utilisateur accepte cette politique.",
    },
    {
      number: 7,
      title: 'Facturation et abonnement',
      content: [
        "7.1 Les tarifs sont définis dans le plan souscrit et communiqués avant toute facturation.",
        "7.2 Les abonnements sont renouvelés automatiquement à chaque période, sauf résiliation préalable.",
        "7.3 En cas de non-paiement, YesMan se réserve le droit de suspendre l'accès au service après mise en demeure.",
        "7.4 Aucun remboursement n'est accordé pour les périodes entamées, sauf disposition légale contraire.",
      ],
    },
    {
      number: 8,
      title: 'Disponibilité du service',
      content: "YesMan s'engage à assurer la disponibilité du service avec un objectif de 99 % de temps de disponibilité mensuel, hors maintenances planifiées notifiées au préalable. YesMan ne saurait être tenu responsable des interruptions dues à des tiers (opérateurs réseau, Meta/WhatsApp, fournisseurs cloud).",
    },
    {
      number: 9,
      title: 'Limitation de responsabilité',
      content: [
        "9.1 YesMan est un outil d'assistance opérationnelle. Les décisions prises sur la base des informations fournies par la plateforme restent sous la responsabilité exclusive de l'utilisateur.",
        "9.2 La responsabilité de YesMan ne saurait excéder le montant des sommes versées par l'utilisateur au cours des trois derniers mois précédant l'incident.",
        "9.3 YesMan ne peut être tenu responsable de pertes indirectes, de manque à gagner ou de préjudices immatériels.",
      ],
    },
    {
      number: 10,
      title: 'Résiliation',
      content: [
        "10.1 L'utilisateur peut résilier son abonnement à tout moment depuis son espace client ou en contactant contact@getyesman.com.",
        "10.2 YesMan peut résilier le contrat sans préavis en cas de violation grave des présentes CGU ou de non-paiement persistant.",
        "10.3 À la résiliation, les données de l'utilisateur sont conservées selon les durées définies dans la Politique de Confidentialité, puis supprimées.",
      ],
    },
    {
      number: 11,
      title: 'Modifications des CGU',
      content: "YesMan se réserve le droit de modifier les présentes CGU à tout moment. L'utilisateur sera notifié au moins 30 jours avant l'entrée en vigueur de toute modification substantielle. La poursuite de l'utilisation du service après notification vaut acceptation des nouvelles conditions.",
    },
    {
      number: 12,
      title: 'Loi applicable et juridiction',
      content: "Les présentes CGU sont soumises au droit de la République Togolaise. Tout litige relatif à leur interprétation ou exécution sera soumis aux juridictions compétentes de Lomé, Togo, à défaut de résolution amiable dans un délai de 30 jours.",
    },
    {
      number: 13,
      title: 'Contact',
      content: 'Pour toute question relative aux présentes CGU : contact@getyesman.com. YesMan — ABIZZUP, Lomé, Togo.',
    },
  ]

  return (
    <div className="min-h-screen bg-black">
      <div className="relative bg-black px-8 py-24 md:py-32">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4 tracking-tight leading-tight text-balance">
            {t.terms.title}
          </h1>
          <p className="text-sm md:text-base text-white/50 font-mono uppercase tracking-wider">
            {t.terms.updated}
          </p>
        </div>
      </div>

      <div className="relative bg-black px-8 pb-20">
        <div className="max-w-4xl mx-auto space-y-8">
          {sections.map((section) => (
            <div
              key={section.number}
              className="border border-white/10 bg-black/50 rounded-lg p-8 hover:border-white/20 transition-colors"
            >
              <div className="flex items-start gap-4 mb-4">
                <span className="inline-flex items-center justify-center w-10 h-10 bg-white/5 border border-white/20 rounded text-white/80 font-mono text-sm font-bold flex-shrink-0">
                  {section.number}
                </span>
                <h2 className="text-xl md:text-2xl font-bold text-white pt-1">
                  {section.title}
                </h2>
              </div>

              <div className="ml-14 space-y-4">
                {Array.isArray(section.content) ? (
                  section.content.map((line, idx) => (
                    <p key={idx} className="text-white/70 text-sm md:text-base leading-relaxed">
                      {renderLine(line)}
                    </p>
                  ))
                ) : (
                  <p className="text-white/70 text-sm md:text-base leading-relaxed">
                    {renderLine(section.content)}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <FooterSection />
    </div>
  )
}
